import { Box, Theme, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import screenfull from 'screenfull';

import { EpisodeResources } from '@/components';
import { AnalyticsService } from '@/services';
import {
  playerActions,
  selectActiveChapterId,
  selectActiveEpisodeId,
  selectEpisodeCompliance,
  selectIsPlayerVideoFullscreen,
  useAppSelector,
  useFetchChapterQuery,
  useFetchEpisodeQuery,
} from '@/store';
import { EpisodeResource } from '@/types';

import {
  TopControls,
  VideoControls,
  VideoNotifications,
  VisualAidViewer,
} from './components';

export const VideoPlayer = ({
  player,
}: {
  player: React.ReactNode;
  trackPlayerRef: React.RefObject<HTMLVideoElement | null>;
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const videoPlayerContainerRef = useRef(undefined);
  const smDownMatch = useMediaQuery(theme.breakpoints.down('sm'));

  const activeChapterId = useAppSelector(selectActiveChapterId)!;
  const activeEpisodeId = useAppSelector(selectActiveEpisodeId)!;
  const compliance = useAppSelector(state =>
    selectEpisodeCompliance(state, activeEpisodeId),
  );
  const { data: episode } = useFetchEpisodeQuery(
    {
      episodeId: activeEpisodeId,
      compliance,
    },
    { skip: !activeEpisodeId },
  );
  const { data: chapter } = useFetchChapterQuery(
    {
      chapterId: activeChapterId,
      compliance,
    },
    { skip: !activeChapterId },
  );
  const isVideoFullscreen = useAppSelector(selectIsPlayerVideoFullscreen);

  useEffect(() => {
    const onScreenfullChange = () => {
      dispatch(playerActions.setVideoFullscreen(screenfull.isFullscreen));
    };

    if (screenfull.isEnabled) {
      screenfull.on('change', onScreenfullChange);
    }

    return () => {
      if (screenfull.isEnabled) {
        screenfull.off('change', onScreenfullChange);
      }
    };
  }, [dispatch]);

  useEffect(() => {
    if (!screenfull.isEnabled) return;

    if (isVideoFullscreen && !screenfull.isFullscreen) {
      screenfull.request(videoPlayerContainerRef.current);
    }

    if (!isVideoFullscreen && screenfull.isFullscreen) {
      screenfull.exit();
    }
  }, [isVideoFullscreen]);

  const onResourceClick = (resource: EpisodeResource) => {
    window.open(resource.file.url);
    AnalyticsService.resourceView({
      resourceName: resource.name,
      episodeId: episode!.episodeId,
    });
  };

  if (!activeChapterId || !activeEpisodeId) {
    return null;
  }

  return (
    <Box sx={styles.root}>
      {smDownMatch && (
        <div>
          <TopControls />
        </div>
      )}
      <Box
        sx={[
          styles.videoPlayer,
          isVideoFullscreen && styles.videoPlayerFullScreen,
        ]}
        ref={videoPlayerContainerRef}>
        {player}
        {chapter?.type === 'audio' ? (
          <VisualAidViewer />
        ) : (
          <>
            <Box sx={styles.videoPlayerControls}>
              <VideoControls />
            </Box>
            <Box sx={styles.videoPlayerOverlay}>
              <VideoNotifications />
            </Box>
          </>
        )}
      </Box>
      <Box sx={styles.videoContent}>
        {!smDownMatch && (
          <div>
            <TopControls />
          </div>
        )}
        <Box sx={{ mt: 3 }}>
          <Typography variant="h3" sx={{ mb: 2, color: 'text.contrast' }}>
            Chapter {chapter?.chapterPosition ?? 1}: {chapter?.name}
          </Typography>
          <Typography variant="body1" sx={styles.episodeDescription}>
            {chapter?.formattedDescription
              ? // TODO
                // convertToMarkdown(
                //   chapter.formattedDescription,
                //   styles.episodeDescription,
                // )
                chapter.formattedDescription
              : chapter?.description}
          </Typography>
          {episode?.resources && episode.resources.length > 0 && (
            <Box>
              <Typography
                variant="h3"
                sx={{ mt: 5, mb: 2, color: 'text.contrast' }}>
                Episode Resources
              </Typography>
              <EpisodeResources
                resources={episode?.resources || []}
                onResourceClick={onResourceClick}
                contrast="dark"
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const styles = {
  root: (theme: Theme) => ({
    color: 'text.contrast',
    display: 'flex',
    flexDirection: 'row',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      height: '100%',
      overflow: 'scroll',
    },
  }),
  videoPlayer: {
    position: 'relative',
    width: {
      xs: '100%',
      sm: '60%',
    },
  },
  videoPlayerFullScreen: {
    width: '100%',
  },
  videoPlayerControls: {
    bottom: 0,
    height: '100%',
    position: 'absolute',
    width: '100%',
  },
  videoPlayerOverlay: {
    bottom: 0,
    height: '100%',
    pointerEvents: 'none',
    position: 'absolute',
    width: '100%',
  },
  videoContent: {
    height: 'auto',
    ml: {
      xs: 0,
      sm: 6,
    },
    width: {
      xs: '100%',
      sm: '40%',
    },
    mt: {
      xs: 2.5,
      sm: 0,
    },
  },
  episodeDescription: {
    color: 'text.contrast',
    mb: 2,
    '& a': {
      color: 'secondary.main',
      textDecoration: 'none',
      '&:hover': {
        color: 'secondary.main',
        textDecoration: 'underline',
      },
      '&:hover, &:focus': {
        color: 'secondary.main',
        textDecoration: 'underline',
      },
    },
  },
};
